import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import moment from "moment";
import TaskSkeleton from "./TaskSkeleton";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

export default function ReminderSummary() {
  const { http, user } = AuthUser();
  const navigate = useNavigate();
  const isSuperAdmin = user && (user.id == 1 || user.id == 3);
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate("/jobcart/reminder");
      return;
    }
    fetchData();
  }, [startDate, endDate]);

  const fetchData = () => {
    setIsLoading(true);
    http
      .get(`/reminder?status=&company=&startdate=${startDate}&enddate=${endDate}&status_filter=`)
      .then((res) => {
        setIsLoading(false);
        if (Array.isArray(res.data.data)) setTasks(res.data.data);
      })
      .catch(() => setIsLoading(false));
  };

  if (!isSuperAdmin) {
    return null;
  }

  // Build summary rows grouped by type
  const buildSummary = () => {
    const grouped = {};
    tasks.forEach((c) => {
      const type = c.type || "Unknown";
      if (!grouped[type]) {
        grouped[type] = { type, count: 0, total_system: 0, total_rate: 0, total_amount: 0 };
      }
      grouped[type].count += 1;
      grouped[type].total_system += Number(c.total_system) || 0;
      grouped[type].total_rate += Number(c.rate) || 0;
      grouped[type].total_amount += Number(c.amount) || 0;
    });
    return Object.values(grouped).sort((a, b) => a.type.localeCompare(b.type));
  };

  const summaryRows = buildSummary();
  const grandCount = tasks.length;
  const grandSystem = summaryRows.reduce((s, r) => s + r.total_system, 0);
  const grandRate = summaryRows.reduce((s, r) => s + r.total_rate, 0);
  const grandAmount = summaryRows.reduce((s, r) => s + r.total_amount, 0);

  const fmt = (n) => Number(n).toLocaleString("en-IN");

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.setTextColor(13, 110, 253);
    doc.text("Reminder Summary Report", 14, 18);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated: ${moment().format("DD MMM YYYY, hh:mm A")}`, 14, 25);

    autoTable(doc, {
      startY: 32,
      head: [["Type", "Count", "Total System", "Total Rate", "Total Amount"]],
      body: [
        ...summaryRows.map((r) => [r.type, r.count, r.total_system, fmt(r.total_rate), fmt(r.total_amount)]),
        ["TOTAL", grandCount, grandSystem, fmt(grandRate), fmt(grandAmount)],
      ],
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [13, 110, 253], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [240, 246, 255] },
      didParseCell: (data) => {
        if (data.row.index === summaryRows.length) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [220, 235, 255];
          data.cell.styles.textColor = [0, 0, 0];
        }
      },
      columnStyles: {
        0: { cellWidth: 55 },
        1: { halign: "center" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
      },
    });

    doc.save(`reminder_summary_${moment().format("YYYYMMDD_HHmm")}.pdf`);
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0 fw-bold">Reminder Summary</h4>
          <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
            Grouped by Type &mdash; {tasks.length} total records
          </p>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate(-1)}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={downloadPDF}
            disabled={summaryRows.length === 0}
          >
            <FontAwesomeIcon icon={faFilePdf} className="me-1" /> Download PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-3 g-2 align-items-end">
        <div className="col-md-2">
          <label className="form-label mb-1" style={{ fontSize: "12px" }}>From Date</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label mb-1" style={{ fontSize: "12px" }}>To Date</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        {(startDate || endDate) && (
          <div className="col-md-1">
            <button
              className="btn btn-outline-secondary btn-sm mt-3"
              onClick={() => { setStartDate(""); setEndDate(""); }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <TaskSkeleton />
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered" style={{ maxWidth: "700px" }}>
            <thead style={{ backgroundColor: "#0d6efd", color: "#fff" }}>
              <tr>
                <th>Type</th>
                <th className="text-center">Count</th>
                <th className="text-end">Total System</th>
                <th className="text-end">Total Rate</th>
                <th className="text-end">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {summaryRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    No data available
                  </td>
                </tr>
              ) : (
                <>
                  {summaryRows.map((r, i) => (
                    <tr key={r.type} style={{ backgroundColor: i % 2 === 0 ? "#f0f6ff" : "#fff" }}>
                      <td className="fw-medium">{r.type}</td>
                      <td className="text-center">{r.count}</td>
                      <td className="text-end">{r.total_system}</td>
                      <td className="text-end">{fmt(r.total_rate)}</td>
                      <td className="text-end">{fmt(r.total_amount)}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: "#dce7ff", fontWeight: "bold" }}>
                    <td>TOTAL</td>
                    <td className="text-center">{grandCount}</td>
                    <td className="text-end">{grandSystem}</td>
                    <td className="text-end">{fmt(grandRate)}</td>
                    <td className="text-end">{fmt(grandAmount)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
